import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiConfiguration } from '../api-configuration';
import { BaseService } from '../base-service';
import {
  AgeGroupItem,
  AgeGroupListFilters,
  AgeGroupPaginatedResult,
  AgeGroupPaginationMeta,
  CreateAgeGroupPayload,
  UpdateAgeGroupPayload,
} from '../models/age-groups-manual.models';

interface ApiEnvelope<T> {
  status: string;
  message?: string;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class AgeGroupsManualService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  list(
    academyId: number | string,
    params: AgeGroupListFilters = {}
  ): Observable<AgeGroupPaginatedResult<AgeGroupItem>> {
    return this.http
      .get<ApiEnvelope<any>>(`${this.rootUrl}/api/v1/academy/${academyId}/age-groups`, {
        params: this.buildParams(params as Record<string, unknown>),
      })
      .pipe(map((response) => this.normalizeList(this.unwrap(response))));
  }

  get(academyId: number | string, id: number): Observable<AgeGroupItem> {
    return this.http
      .get<ApiEnvelope<AgeGroupItem>>(
        `${this.rootUrl}/api/v1/academy/${academyId}/age-groups/${id}`
      )
      .pipe(map((response) => this.unwrap(response)));
  }

  create(academyId: number | string, payload: CreateAgeGroupPayload): Observable<AgeGroupItem> {
    return this.http
      .post<ApiEnvelope<AgeGroupItem>>(
        `${this.rootUrl}/api/v1/academy/${academyId}/age-groups`,
        payload
      )
      .pipe(map((response) => this.unwrap(response)));
  }

  update(
    academyId: number | string,
    id: number,
    payload: UpdateAgeGroupPayload
  ): Observable<AgeGroupItem> {
    return this.http
      .put<ApiEnvelope<AgeGroupItem>>(
        `${this.rootUrl}/api/v1/academy/${academyId}/age-groups/${id}`,
        payload
      )
      .pipe(map((response) => this.unwrap(response)));
  }

  delete(academyId: number | string, id: number): Observable<void> {
    return this.http
      .delete<ApiEnvelope<unknown>>(
        `${this.rootUrl}/api/v1/academy/${academyId}/age-groups/${id}`
      )
      .pipe(map(() => void 0));
  }

  private unwrap<T>(response: ApiEnvelope<T>): T {
    return response?.data as T;
  }

  private buildParams(params: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      httpParams = httpParams.set(key, String(value));
    });

    return httpParams;
  }

  private normalizeList(data: any): AgeGroupPaginatedResult<AgeGroupItem> {
    const rawItems: AgeGroupItem[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.data)
      ? data.data
      : [];

    const meta: AgeGroupPaginationMeta = {
      current_page: Number(data?.meta?.current_page || data?.pagination?.current_page || data?.current_page || 1),
      per_page: Number(data?.meta?.per_page || data?.pagination?.per_page || data?.per_page || 20),
      total: Number(data?.meta?.total || data?.pagination?.total || data?.total || rawItems.length),
      last_page: Number(data?.meta?.last_page || data?.pagination?.last_page || data?.last_page || 1),
    };

    return {
      items: rawItems,
      meta,
    };
  }
}
